/** tm/components/PeekStack.tsx — 3-Panel Peek Stack 컨테이너
 *  P3-1: 모달을 좌→우로 쌓아 peek strip 표시
 *  1단계만: 560px
 *  2단계: 40px peek + 560px active = 600px
 *  3단계: 40px + 40px + 560px = 640px
 */
import React, { useEffect, useRef, useState } from 'react';
import { C } from './ui';

const PANEL_W = 560;
const PEEK_W = 40;
const TRANSITION = '0.3s cubic-bezier(0.4, 0, 0.2, 1)';

export interface PeekPanel {
  id: string;
  title: string;
  content: React.ReactNode;
  /** 활성 상태의 패널 폭. 주지 않으면 560. 리포트 미리보기를 좌측에 띄울 때만 넓힌다. */
  width?: number;
}

interface Props {
  panels: PeekPanel[];
  onPopTo: (idx: number) => void;
  onClose: () => void;
}

export default function PeekStack({ panels, onPopTo, onClose }: Props) {
  const [mounted, setMounted] = useState(false);
  const prevLen = useRef(panels.length);

  // Animate on mount
  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  // Track panel length changes for animation
  useEffect(() => { prevLen.current = panels.length; }, [panels.length]);

  if (panels.length === 0) return null;

  const activeIdx = panels.length - 1;
  const activeW = panels[activeIdx].width || PANEL_W;   // 패널이 요청한 폭 (기본 560)
  const totalWidth = activeIdx * PEEK_W + activeW;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(17,24,39,0.4)',
          backdropFilter: 'blur(2px)',
          opacity: mounted ? 1 : 0,
          transition: `opacity ${TRANSITION}`,
        }}
      />

      {/* Stack container */}
      <div style={{
        position: 'relative',
        width: totalWidth,
        maxWidth: 'calc(100vw - 32px)',
        height: '92vh',
        display: 'flex',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
        transform: mounted ? 'none' : 'translateY(20px) scale(0.97)',
        opacity: mounted ? 1 : 0,
        transition: `transform ${TRANSITION}, opacity ${TRANSITION}, width ${TRANSITION}`,
      }}>
        {panels.map((panel, idx) => {
          const isActive = idx === activeIdx;

          return (
            <div
              key={panel.id}
              style={{
                width: isActive ? activeW : PEEK_W,
                flexShrink: 0,
                position: 'relative',
                overflow: 'hidden',
                transition: `width ${TRANSITION}`,
              }}
            >
              {/* Peek strip overlay — 비활성 패널에만 표시 */}
              {!isActive && (
                <div
                  onClick={(e) => { e.stopPropagation(); onPopTo(idx); }}
                  style={{
                    position: 'absolute', inset: 0, zIndex: 2,
                    background: 'linear-gradient(180deg, #111827 0%, #1B3A5C 100%)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRight: '1px solid rgba(255,255,255,0.08)',
                  }}
                  title={`${panel.title}로 돌아가기`}
                >
                  <div
                    style={{ position: 'absolute', inset: 0, background: 'transparent', transition: 'background 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  />
                  <span style={{
                    writingMode: 'vertical-rl', textOrientation: 'upright',
                    color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 600,
                    letterSpacing: '0.15em', whiteSpace: 'nowrap', userSelect: 'none',
                  }}>
                    {panel.title}
                  </span>
                  <span style={{
                    position: 'absolute', top: 12,
                    left: '50%', transform: 'translateX(-50%)',
                    fontSize: 14, color: 'rgba(255,255,255,0.4)',
                  }}>‹</span>
                </div>
              )}
              {/* Content — 항상 렌더링, 비활성시 display:none으로 state 보존 */}
              <div style={{
                display: isActive ? 'flex' : 'none',
                flexDirection: 'column' as const,
                height: '100%',
                background: C.white,
              }}>
                {panel.content}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
